import React, { PureComponent, useEffect, useState } from "react";
import {
	ScatterChart,
	Scatter,
	XAxis,
	YAxis,
	ZAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Label,
	LabelList,
	ReferenceLine,
} from "recharts";
import { numberFormatter } from "../../utils";

const CostGraph = (props: any) => {
	const [regionMap, setRegionMap] = useState<null | Map<string, number>>(
		null
	);
	const [isSmallNumber, setIsSmallNumber] = useState(false);
	const [minValue, setMinValue] = useState(0);
	const data =
		props.region.length > 0
			? props.data.filter((data: any) =>
					props.region.includes(data.Region)
			  )
			: props.data;

	useEffect(() => {
		if (props.regionalTargetCosts) {
			let map = new Map();
			props.regionalTargetCosts.forEach((d: any) => {
				map.set(d.region, d.cost);
			});
			setRegionMap(map);
		} else {
			setRegionMap(null);
		}
	}, [props.regionalTargetCosts]);

	useEffect(() => {
		if (data.length > 0 && data[0].costPerUnit < 1) {
			setIsSmallNumber(true);
		} else {
			setIsSmallNumber(false);
		}
		let minObj:any = data.length > 0 ? data?.reduce((min:any, current:any) =>
			current.costPerUnit < min.costPerUnit ? current : min
		) : 0;
		setMinValue(minObj.costPerUnit);
		
	}, [data]);
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">Region:</div>
						<div
							className="font-bold ml-1"
							style={{
								color: "#3f3f3f",
								textTransform: "capitalize",
							}}
						>
							{data.Region}
						</div>
					</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								District:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{data.District}
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Net Cost($/unit):{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.costPerUnit, 2, "$")}
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Quantity:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.LastYear_Qty, 0)}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	const updateFill = (data: any) => {
		if (props.regionalTargetCosts) {
			if (
				regionMap?.has(data.Region) &&
				regionMap.get(data.Region) == data.costPerUnit
			) {
				return "rgb(134, 188, 37)";
			}
			return "rgb(98, 181, 229)";
		} else {
			if (data.costPerUnit == props.refLine) {
				return "rgb(134, 188, 37)";
			}
			return "rgb(98, 181, 229)";
		}
	};

	return (
		<ResponsiveContainer className={'p-[4vh] pr-[0]'}>
			<ScatterChart
				width={400}
				height={400}
				margin={{
					top: 10,
					right: 70,
					bottom: 40,
					left: 30,
				}}
			>
				<XAxis
					className="graph-label "
					type="category"
					allowDuplicatedCategory={false}
					dataKey="Region"
					stroke="black"
				/>
				<YAxis
					className="graph-label "
					type="number"
					tickFormatter={(val) =>
						numberFormatter(val, isSmallNumber ? 2 : 0, "$")
					}
					dataKey="costPerUnit"
					stroke="black"
					domain={ minValue <1000 ? ( minValue <100 ? ( minValue<10? ['dataMin', 'dataMax'] : ['dataMin - 10', 'dataMax + 10'])  :['dataMin - 100', 'dataMax + 100']) : ['dataMin - 1000', 'dataMax + 1000']}

				>
					<Label
						value="Net Cost ($/unit)"
						className="graph-label-new"
						stroke="black"
						angle={-90}
						offset={0}
						position="insideLeft"
						style={{ textAnchor: "middle" }}
					/>
				</YAxis>
				<ZAxis type="number" dataKey="LastYear_Qty" range={[50, 500]} />

				{props.refLine && (
					<ReferenceLine
						y={props.refLine}
						label={{
							position: "insideBottomRight",
							fill: "#ce9178",
							fontStyle: "italic",
							value:
								`${
									props.target == "national"
										? "National Target Cost: "
										: "Regional Target Cost: "
								}` + numberFormatter(props.refLine, 2, "$"),
						}}
						stroke="black"
						strokeDasharray="5 3"
						opacity={0.3}
					/>
				)}

				<Tooltip
					contentStyle={{
						color: "black",
						backgroundColor: "rgb(26, 25, 22)",
					}}
					cursor={{ strokeDasharray: "3 3" }}
					content={customTooltip}
				/>
				{data.map((d: any, index: number) => {
					return (
						<Scatter
							key={index}
							name="Cost chart"
							data={[d]}
							fill={updateFill(d)}
						></Scatter>
					);
				})}
			</ScatterChart>
		</ResponsiveContainer>
	);
};

export default CostGraph;
